import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, X, Loader2, ArrowRight, ShieldCheck, ChevronDown, Sparkles, Megaphone,
  Clock, AlertTriangle, MapPin, Star, Globe, MessageCircle, Instagram, Gift, Settings2, Globe2,
} from "lucide-react";
import axios from "axios";
import SEOHead from "@/components/site/SEOHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Contact Maurice -----------------------------------------------------
const WHATSAPP_NUMBER = "23058535782"; // EM Group Maurice
const INSTAGRAM_URL = "https://instagram.com/emgroup.digital";
const REFERRER_OPTIONS_FR = ["Facebook", "Instagram", "Google", "Bouche à oreille", "Autre"];
const REFERRER_OPTIONS_EN = ["Facebook", "Instagram", "Google", "Word of mouth", "Other"];

// --- Meta Pixel — chargé uniquement sur cette LP --------------------------
const META_PIXEL_ID = "1480206357243692";

function loadMetaPixel() {
  if (typeof window === "undefined") return;
  if (window.fbq && window.__emMetaPixelLoaded === META_PIXEL_ID) return;
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
  window.__emMetaPixelLoaded = META_PIXEL_ID;
}

// --- Meta Pixel tracking --------------------------------------------------
// Fires a standard "Lead" event (best for retargeting + Ads Manager optimization)
// + a custom event with the WhatsApp CTA context for granular reporting.
function fireMetaLead(planKey, surface) {
  if (typeof window === "undefined" || !window.fbq) return;
  const planName = planKey === "pro" ? "Pro" : planKey === "essentiel" ? "Essentiel" : (planKey || "n/a");
  const value = planKey === "pro" ? 80 : 50; // valeur approximative en EUR du lead chaud
  try {
    window.fbq("track", "Lead", {
      content_name: `Découverte RS Maurice — ${planName}`,
      content_category: "decouverte-rs-maurice",
      value,
      currency: "EUR",
    });
    window.fbq("trackCustom", "WhatsAppCTAClick", {
      content_name: `Découverte RS Maurice — ${planName}`,
      surface,
      plan: planKey || null,
    });
  } catch (_e) { /* noop */ }
}

// --- i18n dictionary -----------------------------------------------------
const TR = {
  fr: {
    locale: "fr_MU", htmlLang: "fr",
    trust: ["Agence basée à Maurice", "RDV présentiel ou visio disponible", "Français · Créole · English"],
    badge: "Offre découverte — places limitées",
    h1a: "Vos clients sont sur Facebook et Instagram.",
    h1b: "Est-ce que vous y êtes vraiment ?",
    subtitle: "2 mois offerts pour le découvrir — publications, visuels et 1 campagne Meta Ads incluse. Sans engagement.",
    urgency: "Offre limitée — disponible pour un nombre restreint d'entreprises chaque mois, pour garantir un accompagnement de qualité.",
    diffEyebrow: "— Notre différence",
    diffTitle: "Nous sommes basés à Maurice.",
    diffBody: "Pas une agence française qui gère votre compte à distance depuis Paris.\n\nUne vraie équipe locale, disponible pour vous rencontrer en présentiel ou en visio — selon votre préférence.",
    includedEyebrow: "— Inclus dans l'offre",
    includedTitle: "Ce qui est inclus pendant 2 mois",
    included: [
      { ok: true, text: "Publications et visuels professionnels (selon formule choisie)" },
      { ok: true, text: "Rédaction des contenus adaptés à votre secteur" },
      { ok: true, text: "Mise en place d'1 campagne Meta Ads (setup complet inclus)" },
      { ok: true, text: "Landing page dédiée à votre campagne si nécessaire" },
      { ok: false, text: "Budget publicitaire non inclus — vous choisissez votre budget (minimum recommandé : Rs 5 000/mois)" },
    ],
    valueEyebrow: "— Récap valeur",
    valueTitle: "Ce que vous recevez gratuitement",
    val1Label: "2 mois de gestion réseaux sociaux",
    val1Amount: "= Rs 24 000 de valeur offerte",
    val2Label: "1 campagne Meta Ads — setup inclus",
    val2Amount: "= jusqu'à Rs 25 000 de valeur sur le marché",
    valTotal: "Soit jusqu'à",
    valTotalAmount: "Rs 49 000",
    valTotalSuffix: "de valeur offerte pour démarrer — sans rien payer",
    valContext: "À titre de comparaison, le setup d'une campagne Meta Ads est généralement facturé entre Rs 25 000 et Rs 30 000 par les agences spécialisées.",
    plansEyebrow: "— Formules de poursuite",
    plansTitle: "Choisissez votre formule avant de démarrer",
    plansSubtitle: "Votre formule démarre après les 2 mois offerts — 1 mois d'engagement minimum, résiliable ensuite à tout moment avec 30 jours de préavis. Paiement par virement bancaire.",
    plansBottomNote: "Passage à la formule supérieure possible à tout moment. Retour à la formule inférieure non possible en cours d'engagement.",
    planStartCta: "Je démarre gratuitement",
    planNoCharge: "Aucun paiement immédiat — virement après les 2 mois offerts",
    customEyebrow: "Vous avez des besoins spécifiques ?",
    customBody: "Nous créons une offre sur mesure adaptée à votre activité, vos objectifs et votre budget.",
    customCta: "Discutons de votre projet",
    formEyebrow: "— Démarrage",
    formTitle: "On démarre quand vous voulez.",
    formSubtitle: "Remplissez ce formulaire — on vous rappelle sous 24h pour tout organiser.",
    formName: "Nom complet *",
    formCompany: "Nom de l'entreprise *",
    formPhone: "Téléphone (+230) *",
    formEmail: "Email *",
    formPlan: "Formule choisie",
    formReferrer: "Comment avez-vous entendu parler de nous ? (facultatif)",
    formReferrerPlaceholder: "— Choisir —",
    valueBadge: "Jusqu'à Rs 49 000 de valeur offerte",
    formSubmit: "Je démarre mon offre découverte",
    formSubmitLoading: "Envoi…",
    formReassurance: "On vous répond sous 24h pour organiser le démarrage et finaliser le mode de paiement qui vous convient (virement bancaire).",
    successTitle: "Demande bien reçue.",
    successBody: "On vous rappelle sous 24h pour finaliser ensemble le démarrage.",
    contactEyebrow: "— Autres canaux",
    contactTitle: "Vous préférez nous contacter d'abord ?",
    contactWhatsapp: "WhatsApp",
    contactInstagram: "Instagram DM",
    contactNote: "Réponse sous 24h — Sans engagement",
    waLeadMsg: "Bonjour EM Group, je souhaite en savoir plus sur l'offre découverte réseaux sociaux à Maurice.",
    waCustomMsg: "Bonjour EM Group, je souhaite discuter d'une offre personnalisée pour mon entreprise à Maurice.",
    waPlanMsg: (planName, price) => `Bonjour EM Group, je suis intéressé(e) par votre offre découverte réseaux sociaux à Maurice — formule ${planName} (${price}/mois) avec les 2 mois offerts. Pouvez-vous me recontacter ?`,
    cgvIntro: "Conditions Générales de Vente — Offre Découverte Maurice",
    cgvEnNotice: "Terms and Conditions — French only",
    plan1Name: "Essentiel",
    plan2Name: "Pro",
    planBadge: "Le plus choisi",
    seoTitle: "Gestion Réseaux Sociaux à Maurice | 2 mois offerts — EM Group",
    seoDesc: "Développez votre présence sur les réseaux sociaux à Maurice. 2 mois offerts + campagne Meta Ads incluse. Agence basée à Maurice. À partir de Rs 12 000/mois.",
    errFill: "Merci de remplir tous les champs obligatoires.",
    errGeneric: "Une erreur est survenue. Réessayez ou écrivez-nous à contact@emgroupdigital.fr",
  },
  en: {
    locale: "en_MU", htmlLang: "en",
    trust: ["Agency based in Mauritius", "On-site or video meeting available", "French · Creole · English"],
    badge: "Discovery offer — limited spots",
    h1a: "Your customers are on Facebook and Instagram.",
    h1b: "Are you really there?",
    subtitle: "2 months free to find out — posts, visuals and 1 Meta Ads campaign included. No commitment.",
    urgency: "Limited offer — available for a restricted number of businesses each month to ensure quality support.",
    diffEyebrow: "— Our difference",
    diffTitle: "We are based in Mauritius.",
    diffBody: "Not a French agency managing your account remotely from Paris.\n\nA real local team, available to meet you in person or via video call — your choice.",
    includedEyebrow: "— Included in the offer",
    includedTitle: "What's included for 2 months",
    included: [
      { ok: true, text: "Professional posts and visuals (based on chosen plan)" },
      { ok: true, text: "Content writing tailored to your industry" },
      { ok: true, text: "Full Meta Ads campaign setup included" },
      { ok: true, text: "Dedicated campaign landing page if needed" },
      { ok: false, text: "Advertising budget not included — you choose your budget (recommended minimum: Rs 5,000/month)" },
    ],
    valueEyebrow: "— Value recap",
    valueTitle: "What you receive for free",
    val1Label: "2 months social media management",
    val1Amount: "= Rs 24,000 value offered",
    val2Label: "1 Meta Ads campaign — setup included",
    val2Amount: "= up to Rs 25,000 market value",
    valTotal: "Up to",
    valTotalAmount: "Rs 49,000",
    valTotalSuffix: "in value — completely free to start",
    valContext: "For comparison, a Meta Ads campaign setup is generally charged between Rs 25,000 and Rs 30,000 by specialized agencies.",
    plansEyebrow: "— Continuation plans",
    plansTitle: "Choose your plan before starting",
    plansSubtitle: "Your plan starts after the 2 free months — 1 month minimum commitment, then cancel anytime with 30 days notice. Payment by bank transfer.",
    plansBottomNote: "Upgrade possible at any time. Downgrade not possible during commitment.",
    planStartCta: "Start for free",
    planNoCharge: "No immediate payment — bank transfer after the 2 free months",
    customEyebrow: "Do you have specific needs?",
    customBody: "We create a custom offer tailored to your business, goals and budget.",
    customCta: "Let's discuss your project",
    formEyebrow: "— Get started",
    formTitle: "We start when you're ready.",
    formSubtitle: "Fill in this form — we'll call you back within 24h to organize everything.",
    formName: "Full name *",
    formCompany: "Business name *",
    formPhone: "Phone (+230) *",
    formEmail: "Email *",
    formPlan: "Chosen plan",
    formReferrer: "How did you hear about us? (optional)",
    formReferrerPlaceholder: "— Choose —",
    valueBadge: "Up to Rs 49,000 value offered",
    formSubmit: "Start my discovery offer",
    formSubmitLoading: "Sending…",
    formReassurance: "We'll get back to you within 24h to organize the start and finalize the payment method that suits you (bank transfer).",
    successTitle: "Request received.",
    successBody: "We'll call you back within 24h to finalize the start together.",
    contactEyebrow: "— Other channels",
    contactTitle: "Prefer to contact us first?",
    contactWhatsapp: "WhatsApp",
    contactInstagram: "Instagram DM",
    contactNote: "Response within 24h — No commitment",
    waLeadMsg: "Hello EM Group, I would like to know more about the social media discovery offer in Mauritius.",
    waCustomMsg: "Hello EM Group, I would like to discuss a custom offer for my business in Mauritius.",
    waPlanMsg: (planName, price) => `Hello EM Group, I'm interested in your Mauritius social media discovery offer — ${planName} plan (${price}/month) with the 2 free months. Could you contact me back?`,
    cgvIntro: "Conditions Générales de Vente — Offre Découverte Maurice",
    cgvEnNotice: "Terms and Conditions available in French below",
    plan1Name: "Essential",
    plan2Name: "Pro",
    planBadge: "Most popular",
    seoTitle: "Social Media Management Mauritius | 2 months free — EM Group",
    seoDesc: "Grow your social media presence in Mauritius. 2 months free + Meta Ads campaign included. Agency based in Mauritius. From Rs 12,000/month.",
    errFill: "Please fill in all required fields.",
    errGeneric: "An error occurred. Please retry or email us at contact@emgroupdigital.fr",
  },
};

const PLANS = [
  {
    key: "essentiel", price: "Rs 12 000", priceEn: "Rs 12,000",
    featuresFr: [
      { ok: true, text: "3 posts/mois" },
      { ok: true, text: "Rédaction des légendes" },
      { ok: true, text: "Planification et publication" },
      { ok: true, text: "Rapport mensuel simplifié" },
      { ok: true, text: "Délai de réponse : 48h" },
      { ok: false, text: "Stories" },
      { ok: false, text: "Optimisation profil/bio" },
      { ok: false, text: "Community management" },
      { ok: false, text: "Appel mensuel" },
    ],
    featuresEn: [
      { ok: true, text: "3 posts/month" },
      { ok: true, text: "Caption writing" },
      { ok: true, text: "Scheduling & publishing" },
      { ok: true, text: "Simplified monthly report" },
      { ok: true, text: "Response time: 48h" },
      { ok: false, text: "Stories" },
      { ok: false, text: "Profile/bio optimization" },
      { ok: false, text: "Community management" },
      { ok: false, text: "Monthly call" },
    ],
  },
  {
    key: "pro", price: "Rs 22 000", priceEn: "Rs 22,000", highlight: true,
    featuresFr: [
      { ok: true, text: "6 posts/mois" },
      { ok: true, text: "Rédaction des légendes" },
      { ok: true, text: "Planification et publication" },
      { ok: true, text: "2 stories/mois" },
      { ok: true, text: "Optimisation profil/bio mensuelle" },
      { ok: true, text: "Community management 5j/7" },
      { ok: true, text: "Rapport mensuel détaillé + recommandations" },
      { ok: true, text: "Appel suivi mensuel 30 min" },
      { ok: true, text: "Délai de réponse : 24h" },
    ],
    featuresEn: [
      { ok: true, text: "6 posts/month" },
      { ok: true, text: "Caption writing" },
      { ok: true, text: "Scheduling & publishing" },
      { ok: true, text: "2 stories/month" },
      { ok: true, text: "Monthly profile/bio optimization" },
      { ok: true, text: "Community management 5d/7" },
      { ok: true, text: "Detailed monthly report + recommendations" },
      { ok: true, text: "30-min monthly follow-up call" },
      { ok: true, text: "Response time: 24h" },
    ],
  },
];

// CGV — toujours en FR
const CGV_FR = [
  { n: 1, title: "Objet", body: "Les présentes Conditions Générales de Vente régissent l'Offre Découverte EM Group Digital pour Maurice : 2 mois offerts de gestion des réseaux sociaux + 1 campagne Meta Ads incluse." },
  { n: 2, title: "Période découverte de 2 mois", body: "Le client bénéficie de 2 mois entièrement offerts, durant lesquels EM Group fournit :\n\n• Publications et visuels (selon formule choisie)\n• Rédaction des contenus\n• Mise en place d'une campagne Meta Ads (setup inclus)\n• Une landing page dédiée si nécessaire" },
  { n: 3, title: "Choix de la formule", body: "Le client choisit sa formule de poursuite (Essentiel Rs 12 000/mois ou Pro Rs 22 000/mois) avant le démarrage de l'offre découverte.\n\nUn passage vers la formule supérieure est possible à tout moment sur simple demande.\n\nUn retour vers la formule inférieure n'est pas possible en cours d'engagement." },
  { n: 4, title: "Engagement et résiliation", body: "À l'issue des 60 jours offerts, l'abonnement choisi démarre automatiquement.\n\nUn engagement minimum d'1 mois est obligatoire à l'issue de la période découverte.\n\nPassé ce premier mois payant, l'abonnement est résiliable à tout moment avec un préavis de 30 jours calendaires, notifié par email à contact@emgroupdigital.fr." },
  { n: 5, title: "Prestations incluses dans l'abonnement", body: "Essentiel (Rs 12 000/mois) : 3 posts/mois, légendes, planification, rapport mensuel simplifié, délai de réponse 48h.\n\nPro (Rs 22 000/mois) : 6 posts/mois, légendes, planification, 2 stories/mois, optimisation profil mensuelle, community management 5j/7, rapport détaillé, appel mensuel 30 min, délai de réponse 24h." },
  { n: 6, title: "Limites de l'offre", body: "L'offre est limitée à un nombre restreint d'entreprises chaque mois pour garantir un accompagnement de qualité.\n\nEM Group se réserve le droit de refuser l'offre découverte à toute entreprise dont l'activité n'est pas compatible avec nos valeurs ou nos capacités opérationnelles." },
  { n: 7, title: "Validation des contenus", body: "Tous les contenus produits par EM Group sont soumis à validation du client avant publication.\n\nLe client dispose de 48h ouvrées pour valider ou demander des modifications.\n\nPassé ce délai sans retour, les contenus sont considérés validés et publiés selon la planification convenue." },
  { n: 8, title: "Campagne Meta Ads", body: "EM Group met en place une campagne Meta Ads complète (création des visuels publicitaires, rédaction des textes, paramétrage du ciblage, suivi des performances) pendant les 2 mois offerts.\n\nLe budget publicitaire dépensé sur Meta n'est PAS inclus et reste à la charge du client (minimum recommandé : Rs 5 000/mois).\n\nLe client conserve la propriété de son compte Meta Business et de ses données publicitaires." },
  { n: 9, title: "Landing page dédiée", body: "Si la campagne nécessite une landing page dédiée, EM Group la conçoit et l'héberge sur emgroupdigital.fr/lp/[nom-client] pendant la durée de l'abonnement.\n\nÀ la fin de l'abonnement, la landing page est désactivée. Une copie statique peut être fournie au client sur demande." },
  { n: 10, title: "Propriété intellectuelle", body: "Le client conserve la propriété de sa marque, son logo et ses contenus existants fournis à EM Group.\n\nLes visuels et contenus créés par EM Group sont la propriété du client à compter du paiement intégral de la prestation.\n\nEM Group se réserve le droit d'utiliser ces créations à des fins de portfolio et de communication, sauf demande contraire du client." },
  { n: 11, title: "Paiement", body: "Le paiement s'effectue par virement bancaire ou tout autre mode convenu entre les parties avant le démarrage. Aucun prélèvement automatique n'est mis en place pour les clients mauriciens. Les modalités de paiement sont finalisées lors du premier échange téléphonique ou en présentiel." },
  { n: 12, title: "Droit applicable", body: "Les présentes CGV sont soumises au droit français applicable aux prestations fournies par EM Group Digital. Pour les clients basés à Maurice, les parties s'engagent à rechercher une solution amiable avant tout recours judiciaire." },
];

const SECTORS_FR = ["Restaurant/Café", "Salon de beauté/coiffure", "E-commerce/Retail", "Hôtel/Hébergement", "Immobilier", "Santé/Bien-être", "Sport/Loisirs", "Autre"];
const SECTORS_EN = ["Restaurant/Café", "Beauty salon/Hair", "E-commerce/Retail", "Hotel/Accommodation", "Real Estate", "Health/Wellness", "Sport/Leisure", "Other"];

export default function DecouverteRSMaurice() {
  const [lang, setLang] = useState("fr");
  const t = TR[lang];
  const sectorList = useMemo(() => (lang === "fr" ? SECTORS_FR : SECTORS_EN), [lang]);
  const referrerList = useMemo(() => (lang === "fr" ? REFERRER_OPTIONS_FR : REFERRER_OPTIONS_EN), [lang]);

  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", plan: "pro", referrer_source: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [openCgv, setOpenCgv] = useState(-1);

  // Charge le Meta Pixel (1480206357243692) au montage de la page
  useEffect(() => { loadMetaPixel(); }, []);

  const waLeadUrl = useMemo(
    () => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t.waLeadMsg)}`,
    [lang],
  );
  const waCustomUrl = useMemo(
    () => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t.waCustomMsg)}`,
    [lang],
  );

  const scrollToForm = () => {
    document.getElementById("formulaire")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.company.trim() || !form.phone.trim() || !form.email.trim()) {
      setError(t.errFill); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/decouverte-rs-maurice`, { ...form, language: lang });
      // Meta Pixel — événement StartTrial (essai gratuit démarré)
      if (typeof window !== "undefined" && window.fbq) {
        const planName = form.plan === "pro" ? "Pro" : "Essentiel";
        const value = form.plan === "pro" ? 80 : 50;
        try {
          window.fbq("track", "StartTrial", {
            content_name: `Découverte RS Maurice — ${planName}`,
            content_category: "decouverte-rs-maurice",
            value,
            currency: "EUR",
            predicted_ltv: value * 6, // estimation LTV sur 6 mois pour optimisation Meta
          });
        } catch (_e) { /* noop */ }
      }
      fireMetaLead(form.plan, "form-submit");
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || t.errGeneric);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white relative overflow-hidden" data-testid="decouverte-rs-maurice-page">
      {/* Meta Pixel noscript fallback */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          alt=""
          src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
        />
      </noscript>
      <SEOHead
        title={t.seoTitle}
        description={t.seoDesc}
        keywords="gestion réseaux sociaux Maurice, community manager Maurice, social media agency Mauritius, Facebook Instagram Maurice, réseaux sociaux île Maurice entreprise, EM Group Maurice"
        canonical="https://emgroupdigital.fr/decouverte-rs-maurice"
        ogImage="https://emgroupdigital.fr/brand/logo-em-group.png"
        locale={t.locale}
        htmlLang={t.htmlLang}
        robots="index, follow"
        localBusiness={{
          name: "EM Group Maurice",
          description: lang === "fr"
            ? "Agence de gestion des réseaux sociaux à l'île Maurice : community management, création de contenus, campagnes Meta Ads pour TPE/PME mauriciennes."
            : "Social media management agency in Mauritius: community management, content creation, Meta Ads campaigns for Mauritian SMEs.",
          email: "contact@emgroupdigital.fr",
          telephone: "+23058535782",
          url: "https://emgroupdigital.fr/decouverte-rs-maurice",
          image: "https://emgroupdigital.fr/brand/logo-em-group.png",
          areaServed: ["Maurice", "Mauritius", "Île Maurice"],
          address: { "@type": "PostalAddress", addressCountry: "MU" },
        }}
        serviceSchema={{
          name: lang === "fr" ? "Gestion des réseaux sociaux à Maurice" : "Social media management in Mauritius",
          serviceType: "Community management & social media",
          description: lang === "fr"
            ? "Gestion de vos réseaux sociaux à Maurice : 2 mois offerts, publications, visuels, 1 campagne Meta Ads incluse. À partir de Rs 12 000/mois."
            : "Mauritius social media management: 2 months free, posts, visuals, 1 Meta Ads campaign included. From Rs 12,000/month.",
          areaServed: "Maurice",
          price: "12000",
          priceCurrency: "MUR",
        }}
      />

      {/* Ambient gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#E52431]/22 blur-[120px]" />
        <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full bg-amber-500/[0.07] blur-[120px]" />
      </div>

      {/* HEADER */}
      <header className="relative z-10 px-6 md:px-10 pt-6 md:pt-7 flex items-center justify-between">
        <a href="https://emgroupdigital.fr" data-testid="logo-link" className="inline-flex items-center gap-2 group">
          <img src="/brand/logo-em-group.png" alt="EM Group" className="w-10 h-10 rounded-full" />
          <span className="font-outfit text-base font-medium tracking-tight text-white/90 group-hover:text-white transition">EM Group</span>
        </a>
        <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] p-1" data-testid="lang-switcher">
          <button
            onClick={() => setLang("fr")}
            data-testid="lang-fr"
            className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.12em] font-bold transition ${lang === "fr" ? "bg-[#E52431] text-white" : "text-white/55 hover:text-white"}`}
          >
            <Globe2 size={10} className="inline -mt-0.5 mr-1" /> FR
          </button>
          <button
            onClick={() => setLang("en")}
            data-testid="lang-en"
            className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.12em] font-bold transition ${lang === "en" ? "bg-[#E52431] text-white" : "text-white/55 hover:text-white"}`}
          >
            EN
          </button>
        </div>
      </header>

      {/* TRUST BAR */}
      <section className="relative z-10 mt-6 mx-6 md:mx-10 rounded-2xl bg-white/[0.045] border border-white/10 px-5 py-3 flex flex-wrap gap-x-6 gap-y-2 items-center justify-center text-xs text-white/75" data-testid="trust-bar">
        <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-[#FFB4BB]" /> {t.trust[0]}</span>
        <span className="inline-flex items-center gap-1.5"><Star size={12} className="text-amber-300" /> {t.trust[1]}</span>
        <span className="inline-flex items-center gap-1.5"><Globe size={12} className="text-emerald-300" /> {t.trust[2]}</span>
      </section>

      {/* MAIN */}
      <main className="relative z-10 px-6 md:px-10 py-14 md:py-20 max-w-5xl mx-auto">
        {/* HERO */}
        <motion.section
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          data-testid="hero-section"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E52431]/15 border border-[#E52431]/30 text-[#FFB4BB] text-[10px] uppercase tracking-[0.15em] font-bold">
            <Sparkles size={11} /> {t.badge}
          </div>
          <h1 className="mt-6 font-outfit text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
            {t.h1a}<br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E52431] to-[#FF6A75] font-medium">{t.h1b}</span>
          </h1>
          <p className="mt-6 text-white/75 text-base md:text-xl leading-relaxed max-w-3xl">{t.subtitle}</p>
          <p className="mt-4 text-amber-200/85 text-sm md:text-base leading-relaxed max-w-3xl"><AlertTriangle size={13} className="inline -mt-0.5 mr-1" /> {t.urgency}</p>
        </motion.section>

        {/* DIFFERENCE BLOCK */}
        <section className="mt-14 md:mt-20" data-testid="diff-section">
          <div className="rounded-3xl border-2 border-[#E52431]/40 bg-gradient-to-br from-[#E52431]/[0.08] via-[#1a0608] to-[#0A0A0E] p-7 md:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-16 w-72 h-72 bg-[#E52431]/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative flex flex-col md:flex-row gap-6">
              <div className="w-16 h-16 shrink-0 rounded-2xl bg-[#E52431]/15 border border-[#E52431]/40 flex items-center justify-center">
                <MapPin size={32} className="text-[#FFB4BB]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB4BB] font-semibold">{t.diffEyebrow}</div>
                <h2 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight">{t.diffTitle}</h2>
                <p className="mt-4 text-white/80 text-base md:text-lg leading-relaxed whitespace-pre-line">{t.diffBody}</p>
              </div>
            </div>
          </div>
        </section>

        {/* INCLUSIONS */}
        <section className="mt-16 md:mt-20" data-testid="inclusions-section">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold">{t.includedEyebrow}</div>
          <h2 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight">{t.includedTitle}</h2>
          <ul className="mt-8 space-y-3">
            {t.included.map((it, i) => (
              <li key={i} className="flex items-start gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.025]" data-testid={`inclusion-${i}`}>
                {it.ok
                  ? <Check size={18} className="text-emerald-400 mt-0.5 shrink-0" />
                  : <AlertTriangle size={18} className="text-amber-300 mt-0.5 shrink-0" />}
                <span className={it.ok ? "text-white/85" : "text-amber-200/85"}>{it.text}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* VALUE RECAP */}
        <section className="mt-16 md:mt-20" data-testid="value-recap-section">
          <div className="rounded-3xl border border-[#E52431]/35 bg-gradient-to-br from-[#E52431]/[0.10] via-[#1a0608] to-[#0A0A0E] p-6 md:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-16 w-72 h-72 bg-[#E52431]/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB4BB] font-semibold">{t.valueEyebrow}</div>
              <h2 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight">{t.valueTitle}</h2>
              <ul className="mt-8 space-y-4">
                <li className="flex items-start gap-4 p-4 md:p-5 rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="value-item-1">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center"><Check size={18} className="text-emerald-300" /></div>
                  <div className="flex-1">
                    <div className="text-sm md:text-base text-white/85 leading-snug">{t.val1Label}</div>
                    <div className="mt-1 font-outfit text-xl md:text-2xl font-medium text-[#FF6A75]">{t.val1Amount}</div>
                  </div>
                </li>
                <li className="flex items-start gap-4 p-4 md:p-5 rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="value-item-2">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center"><Check size={18} className="text-emerald-300" /></div>
                  <div className="flex-1">
                    <div className="text-sm md:text-base text-white/85 leading-snug">{t.val2Label}</div>
                    <div className="mt-1 font-outfit text-xl md:text-2xl font-medium text-[#FF6A75]">{t.val2Amount}</div>
                  </div>
                </li>
              </ul>
              <div className="mt-8 flex items-center gap-3 p-5 rounded-2xl bg-gradient-to-r from-[#E52431]/20 via-[#E52431]/10 to-transparent border border-[#E52431]/40" data-testid="value-total">
                <ArrowRight size={22} className="text-[#FFB4BB] shrink-0" />
                <p className="text-base md:text-lg leading-relaxed text-white">
                  {t.valTotal} <strong className="font-outfit text-2xl md:text-3xl text-[#FF6A75]">{t.valTotalAmount}</strong> {t.valTotalSuffix}.
                </p>
              </div>
              <p className="mt-5 text-xs md:text-sm text-white/55 leading-relaxed italic">{t.valContext}</p>
            </div>
          </div>
        </section>

        {/* PLANS */}
        <section className="mt-16 md:mt-20" data-testid="plans-section">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold">{t.plansEyebrow}</div>
            <h2 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight">{t.plansTitle}</h2>
            <p className="mt-3 text-sm md:text-base text-white/65 leading-relaxed">{t.plansSubtitle}</p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {PLANS.map((p, i) => {
              const features = lang === "fr" ? p.featuresFr : p.featuresEn;
              const price = lang === "fr" ? p.price : p.priceEn;
              const name = i === 0 ? t.plan1Name : t.plan2Name;
              return (
                <div
                  key={p.key}
                  data-testid={`plan-${p.key}`}
                  className={`relative rounded-3xl border p-7 md:p-8 ${p.highlight ? "border-[#E52431]/55 bg-gradient-to-br from-[#E52431]/[0.10] to-white/[0.02]" : "border-white/10 bg-white/[0.02]"}`}
                >
                  {p.highlight && (
                    <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[#E52431] text-white text-[10px] uppercase tracking-[0.15em] font-bold">{t.planBadge}</div>
                  )}
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold">{name}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-outfit text-4xl md:text-5xl font-light tracking-tight">{price}</span>
                    <span className="text-sm text-white/55">{lang === "fr" ? "/mois TTC" : "/month"}</span>
                  </div>
                  <ul className="mt-6 space-y-2.5">
                    {features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2.5 text-sm">
                        {f.ok ? <Check size={14} className="text-emerald-400 mt-0.5 shrink-0" /> : <X size={14} className="text-white/25 mt-0.5 shrink-0" />}
                        <span className={f.ok ? "text-white/85" : "text-white/35 line-through"}>{f.text}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => {
                      fireMetaLead(p.key, "plan-card");
                      const waMsg = encodeURIComponent(t.waPlanMsg(name, price));
                      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`, "_blank", "noopener,noreferrer");
                    }}
                    data-testid={`plan-${p.key}-cta`}
                    className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-xs uppercase tracking-[0.12em] font-bold transition ${p.highlight ? "bg-gradient-to-r from-[#E52431] to-[#FF6A75] text-white hover:shadow-[0_0_40px_rgba(229,36,49,0.45)]" : "border border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"}`}
                  >
                    {t.planStartCta} <ArrowRight size={14} />
                  </button>
                  <p className="mt-3 text-center text-[10px] text-white/45 leading-relaxed">
                    <ShieldCheck size={10} className="inline -mt-0.5 mr-0.5 text-emerald-300" /> {t.planNoCharge}
                  </p>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-center text-xs text-white/45 italic max-w-2xl mx-auto leading-relaxed">{t.plansBottomNote}</p>

          {/* CUSTOM OFFER */}
          <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.025] p-6 md:p-8 backdrop-blur-xl max-w-3xl mx-auto" data-testid="custom-offer-block">
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <div className="w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br from-[#E52431]/20 to-[#FF6A75]/10 border border-[#E52431]/30 flex items-center justify-center">
                <Settings2 size={26} className="text-[#FFB4BB]" />
              </div>
              <div className="flex-1">
                <h3 className="font-outfit text-xl md:text-2xl font-light tracking-tight">{t.customEyebrow}</h3>
                <p className="mt-2 text-sm md:text-base text-white/65 leading-relaxed">{t.customBody}</p>
              </div>
              <a
                href={waCustomUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fireMetaLead(null, "custom-offer")}
                data-testid="custom-offer-cta"
                className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white px-5 py-3 text-xs uppercase tracking-[0.12em] font-bold transition"
              >
                {t.customCta} <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </section>

        {/* FORM */}
        <section id="formulaire" className="mt-20 md:mt-24" data-testid="form-section">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold">{t.formEyebrow}</div>
          <h2 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight">{t.formTitle}</h2>
          <p className="mt-3 text-sm md:text-base text-white/65 leading-relaxed max-w-2xl">{t.formSubtitle}</p>

          <div className="mt-8 max-w-2xl rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-6 md:p-8">
            {!submitted ? (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="lead-form">
                <input data-testid="form-name" type="text" placeholder={t.formName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#E52431]/60 outline-none" />
                <input data-testid="form-company" type="text" placeholder={t.formCompany} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#E52431]/60 outline-none" />
                <input data-testid="form-phone" type="tel" placeholder={t.formPhone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#E52431]/60 outline-none" />
                <input data-testid="form-email" type="email" placeholder={t.formEmail} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#E52431]/60 outline-none" />
                {/* Plan radio */}
                <div>
                  <div className="text-[11px] uppercase tracking-[0.1em] text-white/55 font-medium mb-2">{t.formPlan}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {PLANS.map((p, i) => {
                      const price = lang === "fr" ? p.price : p.priceEn;
                      const name = i === 0 ? t.plan1Name : t.plan2Name;
                      return (
                        <label key={p.key} className={`cursor-pointer p-3 rounded-xl border text-center transition ${form.plan === p.key ? "border-[#E52431]/60 bg-[#E52431]/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"}`}>
                          <input type="radio" name="plan" value={p.key} checked={form.plan === p.key} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="hidden" data-testid={`plan-radio-${p.key}`} />
                          <div className="text-xs font-semibold text-white">{name}</div>
                          <div className="text-[10px] text-white/55 mt-0.5">{price}{lang === "fr" ? "/mois" : "/month"}</div>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.1em] text-white/55 font-medium mb-2">{t.formReferrer}</div>
                  <select data-testid="form-referrer" value={form.referrer_source} onChange={(e) => setForm({ ...form, referrer_source: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-[#E52431]/60 outline-none">
                    <option value="">{t.formReferrerPlaceholder}</option>
                    {referrerList.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                  </select>
                </div>
                {error && <div className="p-3 rounded-xl bg-[#E52431]/10 border border-[#E52431]/30 text-sm text-[#FFB4BB]" data-testid="form-error">{error}</div>}

                <div className="flex justify-center" data-testid="cta-value-badge">
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#E52431]/[0.10] border border-[#E52431]/30 text-[11px] uppercase tracking-[0.12em] font-bold text-[#FFB4BB]">
                    <Gift size={11} /> {t.valueBadge}
                  </div>
                </div>

                <button type="submit" disabled={loading} data-testid="form-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E52431] to-[#FF6A75] text-white font-semibold px-6 py-4 text-sm uppercase tracking-[0.12em] hover:shadow-[0_0_40px_rgba(229,36,49,0.45)] transition disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  {loading ? t.formSubmitLoading : t.formSubmit}
                </button>
                <p className="text-[11px] text-white/55 text-center leading-relaxed pt-1">
                  <ShieldCheck size={11} className="inline -mt-0.5 mr-1 text-emerald-300" /> {t.formReassurance}
                </p>
              </form>
            ) : (
              <div className="text-center py-8" data-testid="form-success">
                <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 mx-auto flex items-center justify-center"><Check size={28} className="text-emerald-300" /></div>
                <h3 className="mt-5 font-outfit text-2xl font-light tracking-tight">{t.successTitle}</h3>
                <p className="mt-2 text-white/65 text-sm leading-relaxed">{t.successBody}</p>
              </div>
            )}
          </div>

          {/* SECONDARY CONTACT */}
          <div className="max-w-2xl mt-10 text-center mx-auto" data-testid="secondary-contact-section">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold">{t.contactEyebrow}</div>
            <h3 className="mt-2 font-outfit text-xl md:text-2xl font-light tracking-tight">{t.contactTitle}</h3>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <a href={waLeadUrl} target="_blank" rel="noopener noreferrer" data-testid="secondary-whatsapp-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/[0.05] hover:bg-emerald-500/[0.12] text-white px-5 py-3.5 text-sm font-medium transition">
                <MessageCircle size={16} className="text-emerald-300" /> {t.contactWhatsapp}
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="secondary-instagram-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#E1306C]/35 bg-[#E1306C]/[0.05] hover:bg-[#E1306C]/[0.12] text-white px-5 py-3.5 text-sm font-medium transition">
                <Instagram size={16} className="text-[#F5A6C4]" /> {t.contactInstagram}
              </a>
            </div>
            <p className="mt-5 text-xs text-white/45 leading-relaxed"><Clock size={11} className="inline -mt-0.5 mr-1" /> {t.contactNote}</p>
          </div>
        </section>
      </main>

      {/* CGV — toujours en FR */}
      <section className="relative z-10 bg-[#06060A] border-t border-white/5 px-6 md:px-10 py-14" data-testid="cgv-section">
        <div className="max-w-3xl mx-auto">
          {lang === "en" && (
            <p className="mb-4 text-[11px] uppercase tracking-[0.15em] text-amber-300/85 font-semibold" data-testid="cgv-en-notice">
              ℹ {TR.en.cgvEnNotice}
            </p>
          )}
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">— Cadre légal</div>
          <h2 className="mt-2 font-outfit text-2xl md:text-3xl font-light tracking-tight">{TR.fr.cgvIntro}</h2>
          <div className="mt-6 rounded-3xl border border-white/8 bg-white/[0.015] divide-y divide-white/5 overflow-hidden">
            {CGV_FR.map((art, i) => {
              const isOpen = openCgv === i;
              return (
                <div key={art.n} data-testid={`cgv-article-${art.n}`}>
                  <button type="button" onClick={() => setOpenCgv(isOpen ? -1 : i)} aria-expanded={isOpen} className="w-full text-left flex items-start gap-4 px-5 md:px-7 py-4 hover:bg-white/[0.015] transition">
                    <span className="shrink-0 mt-0.5 text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">Art. {art.n}</span>
                    <span className="flex-1 text-sm md:text-base font-medium text-white/85">{art.title}</span>
                    <ChevronDown size={14} className={`shrink-0 mt-1 text-white/45 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 md:px-7 pb-5 text-xs md:text-sm text-white/65 leading-relaxed whitespace-pre-line">{art.body}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 px-6 md:px-10 py-8 text-[11px] text-white/35 text-center">
        © {new Date().getFullYear()} EM Group Digital — <a href="mailto:contact@emgroupdigital.fr" className="hover:text-white/60 transition">contact@emgroupdigital.fr</a>
      </footer>
    </div>
  );
}
